import React, { useEffect, useState } from "react";
import { Typography, Tag, Button, Space, Row, Col, Card, Divider } from "antd";
import { XCircle, FileText, Truck } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import useRequest from "../hooks/useRequest";
import apiPath from "../constants/apiPath";
import notFoundImage from "../assets/images/not_found.png";

const { Title, Text } = Typography;

const OrderDetails = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { request } = useRequest();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);

    const formatMoney = (value) => {
        if (value === null || value === undefined || value === "") return "-";
        const numeric = Number(value);
        return Number.isFinite(numeric) ? `${numeric}` : `${value}`;
    };

    const normalizeImageUrl = (url) => {
        if (!url) return "";

        if (Array.isArray(url)) {
            return normalizeImageUrl(url.find(Boolean));
        }

        if (typeof url === "object") {
            return normalizeImageUrl(
                url.url ||
                url.image ||
                url.image_url ||
                url.imageUrl ||
                url.src ||
                url.path
            );
        }

        const trimmed = String(url).trim();
        if (!trimmed) return "";
        if (
            trimmed.startsWith("http://") ||
            trimmed.startsWith("https://") ||
            trimmed.startsWith("data:") ||
            trimmed.startsWith("blob:")
        ) {
            return trimmed;
        }
        if (trimmed.startsWith("//")) return window.location.protocol + trimmed;

        return `${apiPath.baseURL.replace(/\/$/, "")}/${trimmed.replace(/^\//, "")}`;
    };

    const normalizeOrderPayload = (data) => {
        const payload = data?.data ?? data;
        return payload?.order ?? payload?.data ?? payload ?? null;
    };

    const getOrderItems = (orderData) => {
        if (!orderData) return [];
        return orderData.items ?? orderData.order_items ?? orderData.products ?? [];
    };

    const getItemName = (item) => {
        return item?.name || item?.product_name || item?.title || item?.product?.name || item?.product?.title || "Product";
    };

    const getItemSku = (item) => {
        return item?.sku || item?.product_sku || item?.product_source_id || item?.product?.sku || item?.product?.product_sku || "N/A";
    };

    const getItemQty = (item) => {
        return item?.quantity || item?.qty || item?.count || 1;
    };

    const getItemUnitPrice = (item) => {
        const value =
            item?.unit_price ??
            item?.price ??
            item?.sale_price ??
            item?.salePrice ??
            item?.product?.price ??
            item?.product?.sale_price ??
            item?.product?.salePrice ??
            item?.subtotal ??
            item?.sub_total ??
            item?.total_amount ??
            item?.line_total ??
            item?.line_total_amount;

        const numeric = Number(value);
        return Number.isFinite(numeric) ? numeric : value ?? "-";
    };

    const getItemSubtotal = (item) => {
        const value =
            item?.subtotal ??
            item?.sub_total ??
            item?.total_amount ??
            item?.line_total ??
            item?.line_total_amount ??
            item?.amount ??
            item?.price ??
            item?.unit_price ??
            item?.product?.price;

        const numeric = Number(value);
        return Number.isFinite(numeric) ? numeric : value ?? "-";
    };

    const getItemImage = (item) => {
        const candidates = [
            item?.image,
            item?.image_url,
            item?.imageUrl,
            item?.product_image,
            item?.thumbnail,
            item?.thumbUrl,
            item?.url,
            item?.product?.image,
            item?.product?.image_url,
            item?.product?.imageUrl,
            item?.product?.thumbnail,
            item?.product?.images,
        ];

        const found = candidates.find((src) => {
            if (Array.isArray(src)) return src.length > 0;
            return typeof src === "string" ? src.trim() !== "" : Boolean(src);
        });

        return normalizeImageUrl(found) || notFoundImage;
    };

    useEffect(() => {
        request({
            url: `${apiPath.orders}${id}/`,
            method: "GET",
            onSuccess: (data) => {
                setOrder(normalizeOrderPayload(data));
                setLoading(false);
            },
            onError: () => {
                setLoading(false);
            }
        });
    }, [id, request]);

    if (loading) {
        return <div>Loading...</div>;
    }

    if (!order) {
        return <div>Order not found</div>;
    }

    const items = getOrderItems(order);
    const subtotal = items.reduce((sum, item) => {
        const value = Number(getItemSubtotal(item));
        return Number.isFinite(value) ? sum + value : sum;
    }, 0);
    const orderTotal = order.grand_total ?? order.total ?? order.amount ?? order.total_amount ?? subtotal;

    return (
        <div className="order-details-container" style={{ padding: '24px', background: '#fff', minHeight: '100vh' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <Space align="center" size="middle">
                    <Title level={4} style={{ margin: 0, fontWeight: 700 }}>Order #{order.order_number || id}</Title>
                    <Tag
                        color={order.status === 'Pending' ? 'warning' : 'success'}
                        style={{
                            borderRadius: '12px',
                            border: 'none',
                            fontWeight: 500,
                            padding: '0 10px',
                            background: order.status === 'Pending' ? '#f59e0b' : '#10b981',
                            color: '#fff',
                            margin: 0
                        }}
                    >
                        {order.status}
                    </Tag>
                </Space>
                <Button type="text" onClick={() => navigate(-1)} style={{ color: '#64748b', fontWeight: 500 }}>
                    Back
                </Button>
            </div>

            <Space size="large" style={{ marginBottom: '24px' }}>
                <Button type="text" icon={<XCircle size={16} />} style={{ color: '#64748b', padding: 0 }}>
                    Cancel
                </Button>
                <Button type="text" icon={<FileText size={16} />} style={{ color: '#64748b', padding: 0 }}>
                    Invoice
                </Button>
                <Button type="text" icon={<Truck size={16} />} style={{ color: '#64748b', padding: 0 }}>
                    Ship
                </Button>
            </Space>

            <Row gutter={24}>
                <Col span={16}>
                    <Card bordered style={{ marginBottom: '24px', borderRadius: '8px' }} bodyStyle={{ padding: 0 }}>
                        <div style={{ padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f0f0f0' }}>
                            <Text strong style={{ color: '#334155' }}>Order Items ({items.length || 0})</Text>
                            <Text strong style={{ color: '#334155' }}>Grand Total - {formatMoney(orderTotal)}</Text>
                        </div>

                        {items.length > 0 ? items.map((item, index) => {
                            const itemName = getItemName(item);
                            const itemSku = getItemSku(item);
                            const itemQty = getItemQty(item);
                            const itemImage = getItemImage(item);
                            const itemUnitPrice = getItemUnitPrice(item);
                            const itemSubtotal = getItemSubtotal(item);

                            return (
                                <div
                                    key={`${itemSku}-${index}`}
                                    style={{
                                        padding: '24px',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        borderBottom: index !== items.length - 1 ? '1px solid #f0f0f0' : 'none'
                                    }}
                                >
                                    <Space align="start" size="large">
                                        <div style={{ width: 60, height: 60, background: '#f1f5f9', borderRadius: 6, overflow: 'hidden' }}>
                                            <img
                                                src={itemImage}
                                                alt={itemName}
                                                onError={(event) => {
                                                    event.currentTarget.onerror = null;
                                                    event.currentTarget.src = notFoundImage;
                                                }}
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                            <Text strong style={{ color: '#0f172a' }}>{itemName}</Text>
                                            <Text style={{ color: '#64748b', fontSize: '13px' }}>
                                                {formatMoney(itemUnitPrice)} Per Unit x {itemQty} Quantity
                                            </Text>
                                            <Text style={{ color: '#64748b', fontSize: '13px' }}>SKU - {itemSku}</Text>
                                            <Text style={{ color: '#64748b', fontSize: '13px' }}>Ordered ({itemQty})</Text>
                                        </div>
                                    </Space>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', textAlign: 'right' }}>
                                        <Text strong style={{ color: '#0f172a' }}>{formatMoney(itemSubtotal)}</Text>
                                        <Text style={{ color: '#64748b', fontSize: '13px' }}>Price - {formatMoney(itemUnitPrice)}</Text>
                                        <Text style={{ color: '#64748b', fontSize: '13px' }}>0.0000% Tax - 0.00</Text>
                                        <Text style={{ color: '#64748b', fontSize: '13px' }}>Sub Total - {formatMoney(itemSubtotal)}</Text>
                                    </div>
                                </div>
                            );
                        }) : (
                            <div style={{ padding: '24px', color: '#64748b' }}>No items found for this order.</div>
                        )}

                        <div style={{ padding: '24px', display: 'flex', justifyContent: 'flex-end' }}>
                            <div style={{ width: '300px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                    <Text style={{ color: '#64748b' }}>Sub Total</Text>
                                    <Text strong style={{ color: '#334155' }}>{formatMoney(subtotal)}</Text>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                    <Text style={{ color: '#64748b' }}>Tax</Text>
                                    <Text strong style={{ color: '#334155' }}>0.00</Text>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                    <Text style={{ color: '#64748b' }}>Shipping and Handling</Text>
                                    <Text strong style={{ color: '#334155' }}>0.00</Text>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                    <Text strong style={{ color: '#0f172a' }}>Grand Total</Text>
                                    <Text strong style={{ color: '#0f172a' }}>{formatMoney(orderTotal)}</Text>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                    <Text style={{ color: '#64748b' }}>Total Paid</Text>
                                    <Text strong style={{ color: '#334155' }}>{formatMoney(order.total_paid ?? order.paid_amount ?? 0)}</Text>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                    <Text style={{ color: '#64748b' }}>Total Refund</Text>
                                    <Text strong style={{ color: '#334155' }}>{formatMoney(order.total_refund ?? order.refund_amount ?? 0)}</Text>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                    <Text style={{ color: '#64748b' }}>Total Due</Text>
                                    <Text strong style={{ color: '#334155' }}>{formatMoney(order.total_due ?? order.due_amount ?? orderTotal)}</Text>
                                </div>
                            </div>
                        </div>
                    </Card>
                </Col>

                <Col span={8}>
                    <Card bordered style={{ borderRadius: '8px', height: '100%' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <Text strong style={{ color: '#64748b' }}>Customer</Text>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '24px' }}>
                            <Text style={{ color: '#0f172a' }}>{order.customer_name || order.user?.name || order.customer?.name || 'John Doe'}</Text>
                            <Text style={{ color: '#64748b', fontSize: '13px' }}>{order.customer_email || order.user?.email || order.customer?.email || 'john@example.com'}</Text>
                            <Text style={{ color: '#64748b', fontSize: '13px' }}>Customer Group : General</Text>
                        </div>
                        <Divider style={{ margin: '16px 0' }} />

                        <div style={{ marginBottom: '16px' }}>
                            <Text strong style={{ color: '#64748b' }}>Billing Address</Text>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '24px' }}>
                            <Text strong style={{ color: '#0f172a' }}>{order.billing_company || order.billing_name || 'Cox Inc'}</Text>
                            <Text strong style={{ color: '#0f172a' }}>{order.billing_contact || order.billing_contact_name || 'john deo'}</Text>
                            <Text style={{ color: '#64748b', fontSize: '13px' }}>{order.billing_address_line1 || order.shipping_address_line1 || '56 Wrexham Road'}</Text>
                            <Text style={{ color: '#64748b', fontSize: '13px' }}>{order.billing_city || order.shipping_city || 'Featherstone'}</Text>
                            <Text style={{ color: '#64748b', fontSize: '13px' }}>{order.billing_state || order.shipping_state || 'Bilzen'}</Text>
                            <Text style={{ color: '#64748b', fontSize: '13px' }}>{order.billing_country || order.shipping_country || 'United Kingdom (90006)'}</Text>
                            <Text style={{ color: '#64748b', fontSize: '13px' }}>Contact : {order.billing_phone || order.customer_phone || '456454555'}</Text>
                        </div>
                        <Divider style={{ margin: '16px 0' }} />

                        <div style={{ marginBottom: '16px' }}>
                            <Text strong style={{ color: '#64748b' }}>Shipping Address</Text>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <Text strong style={{ color: '#0f172a' }}>{order.shipping_company || order.shipping_name || 'Cox Inc'}</Text>
                            <Text strong style={{ color: '#0f172a' }}>{order.shipping_contact || 'john deo'}</Text>
                            <Text style={{ color: '#64748b', fontSize: '13px' }}>{order.shipping_address_line1 || '56 Wrexham Road'}</Text>
                            <Text style={{ color: '#64748b', fontSize: '13px' }}>{order.shipping_city || 'Featherstone'}</Text>
                        </div>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default OrderDetails;
